import {
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from "react";
import type { ChatMessage } from "@/hooks/retro-api/useRetroChatStream";
import { useRetroChatStream } from "@/hooks/retro-api/useRetroChatStream";
import { Button } from "@/shared/components/ui/button";
import { MessageBubble } from "@/features/evaluacion/components/MessageBubble";
import { useUpdateActividadAprendiz } from "@/hooks/actividadAprendiz/useActividadAprendiz";
import { useCurrentUser } from "@/features/login/hooks/useCurrentUser";
import EMBLEMA from "@/assets/EMBLEMA-RETRO-IA.png";

export interface ChatBoxRef {
  sendMessage: (message: string) => Promise<void>;
}

export const ChatBox = forwardRef<
  ChatBoxRef,
  {
    index: string;
    llm_provider?: string;
    idActividadAprendiz: number;
    sessionId?: string | null;
    historialChat: {
      role: "user" | "assistant";
      content: string;
    }[];
    /** Contenido encima de la caja de texto (p. ej. preguntas sugeridas). */
    sugerencias?: React.ReactNode;
    /** Se llama cuando el coach termina de responder. */
    onRespuestaCompleta?: () => void;
    /** Se llama al enfocar o desenfocar la caja de texto. */
    onFocoComposer?: (enfocado: boolean) => void;
  }
>(
  (
    {
      index,
      llm_provider,
      idActividadAprendiz,
      sessionId,
      historialChat,
      sugerencias,
      onRespuestaCompleta,
      onFocoComposer,
    },
    ref,
  ) => {
    const { messages, loading, error, sendMessage, cancel } =
      useRetroChatStream({
        index: index,
        llm_provider: llm_provider ?? "OpenAI",
        sessionId: sessionId,
        initialHistory: historialChat,
      });
    const { mutate: updateHistorial } = useUpdateActividadAprendiz();
    const { usuarioInfo } = useCurrentUser();
    const [input, setInput] = useState("");
    const [isComposing, setIsComposing] = useState(false);
    const listRef = useRef<HTMLDivElement | null>(null);
    const lastSavedMessagesRef = useRef<string>("");
    const isInitialMountRef = useRef(true);
    const canSend = !loading && input.trim().length > 0;
    useImperativeHandle(
      ref,
      () => ({
        sendMessage: async (message: string) => {
          await sendMessage(message, messages as ChatMessage[], idActividadAprendiz ?? 0);
        },
      }),
      [sendMessage, messages],
    );
    const handleSubmit = async () => {
      const text = input.trim();
      if (!text) return;
      // Clear the input immediately so the user's text disappears right away
      setInput("");
      await sendMessage(text, messages as ChatMessage[], idActividadAprendiz ?? 0);
    };
    const onKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
      if (isComposing) return;
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (canSend) handleSubmit();
      }
    };
    // Avisa cuando termina una respuesta (loading pasa de true a false).
    const estabaCargandoRef = useRef(false);
    useEffect(() => {
      if (estabaCargandoRef.current && !loading) onRespuestaCompleta?.();
      estabaCargandoRef.current = loading;
    }, [loading, onRespuestaCompleta]);
    useEffect(() => {
      listRef.current?.scrollTo({
        top: listRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, [messages, loading]);
    // Guardar historial cuando se completa un mensaje (loading pasa a false y hay mensajes nuevos)
    useEffect(() => {
      if (!idActividadAprendiz || idActividadAprendiz === 0) return;

      const messagesStr = JSON.stringify(messages);
      const hasMessages = messages.length > 0;
      const isDifferentFromLastSaved =
        messagesStr !== lastSavedMessagesRef.current;
      if (
        !loading &&
        hasMessages &&
        isDifferentFromLastSaved &&
        !isInitialMountRef.current
      ) {
        updateHistorial({
          idActividad: idActividadAprendiz,
          historialChat: messages,
        });
        lastSavedMessagesRef.current = messagesStr;
      }

      if (hasMessages && isInitialMountRef.current) {
        isInitialMountRef.current = false;
        if (historialChat.length > 0) {
          lastSavedMessagesRef.current = JSON.stringify(historialChat);
        }
      }
    }, [
      loading,
      idActividadAprendiz,
      messages,
      updateHistorial,
      historialChat,
    ]);
    const nombreUsuario =
      usuarioInfo?.aprendiz?.usuario?.nombre ||
      usuarioInfo?.nombre ||
      "Aprendiz";
    return (
      <div className="flex h-full min-h-0 flex-col relative">
        <div
          ref={listRef}
          className="flex-1 min-h-0 overflow-y-auto px-4 py-5 md:px-5 relative z-10 scrollbar-primary"
          style={{
            background: "transparent",
          }}
          aria-live="polite"
        >
          {messages.length === 0 && !loading ? (
            <div className="flex flex-col items-center justify-center h-full">
              <img
                src={EMBLEMA}
                alt=""
                aria-hidden="true"
                className="w-24 md:w-28 h-auto object-contain opacity-90"
              />
              <div className="text-center pt-6 max-w-xs">
                <p
                  className="text-2xl font-bold mb-2"
                  style={{ color: "var(--primary)" }}
                >
                  Hola, {nombreUsuario}
                </p>
                <p className="text-base text-[var(--foreground)] opacity-70">
                  Pregúntame lo que quieras sobre tu retroalimentación. Te ayudo a
                  entenderla y a aplicarla en tu reto.
                </p>
              </div>
            </div>
          ) : (
            <>
              {messages.map((m, i) => (
                <MessageBubble
                  key={i}
                  role={m.role}
                  content={m.content}
                  kind={m.kind}
                />
              ))}
            </>
          )}
          {error && <div className="text-red-500 text-sm">⚠️ {error}</div>}
        </div>
        {sugerencias}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSend) handleSubmit();
          }}
          className="mt-2 mx-4 mb-4 flex items-end gap-2 p-3 bg-[var(--card-background)] rounded-4xl shadow-md/30 border border-[var(--border)] relative z-10"
        >
          <label htmlFor="chat-input" className="sr-only">
            Escribe tu mensaje
          </label>
          <textarea
            id="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            onFocus={() => onFocoComposer?.(true)}
            onBlur={() => onFocoComposer?.(false)}
            onCompositionStart={() => setIsComposing(true)}
            onCompositionEnd={() => setIsComposing(false)}
            rows={1}
            placeholder="Pregunta lo que desees."
            aria-disabled={loading}
            aria-label="Mensaje para enviar al asistente"
            className="flex-1 resize-none bg-transparent border-0 p-2 text-sm focus:outline-none focus:ring-0 text-[var(--foreground)] placeholder:text-[var(--foreground)] placeholder:opacity-40"
          />
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "flex-end",
              alignItems: "center",
              flexDirection: "row",
            }}
          >
            {loading && (
              <button
                type="button"
                onClick={cancel}
                aria-label="Cancelar generación"
                title="Cancelar"
                className="w-9 h-9 p-0 flex items-center justify-center rounded-full hover:bg-red-50 border-2 border-red-600 bg-white focus:outline-none focus:ring-2 focus:ring-red-200"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-4 h-4 text-red-600"
                  aria-hidden="true"
                >
                  <rect x="5" y="5" width="14" height="14" rx="4" />
                </svg>
              </button>
            )}
            <Button
              type="submit"
              disabled={!canSend}
              variant={"default"}
              className="rounded-full w-10 h-10 p-0 flex items-center justify-center"
              style={{ backgroundColor: "var(--primary)" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="w-5 h-5 text-white"
              >
                <path d="M12 19V5M5 12l7-7 7 7" />
              </svg>
            </Button>
          </div>
        </form>
      </div>
    );
  },
);

ChatBox.displayName = "ChatBox";
